import React, { useCallback, useEffect } from "react";
import chevronLeft from "@assets/icon-chevron-left-white.svg";
import chevronRight from "@assets/icon-chevron-right-white.svg";
import { REMOVE_FOCUS_CSS } from "@components/Button/utils/constants";
import useSendPulseGaData from "@features/pulse/hooks/useSendPulseGaData";
import PulseEndingScreen from "@features/pulse/variant/control/components/PulseEndingScreen";
import { PulseDesktopPageProps } from "@features/pulse/variant/control/page/PulseDesktopPage";
import {
  ARROW_SIZE,
  ARROW_TOTAL_WIDTH,
  CARD_WIDTH,
  PADDING,
  SMALL_CARD_WIDTH,
} from "@features/pulse/variant/control/utils/constants";
import { getTheCardLeftCss } from "@features/pulse/variant/control/utils/helpers";
import { cn } from "@util/helpers";

import PulseIndividualArticle from "./PulseIndividualDesktopArticle";

export default function PulseDesktopCarousel({
  sortedPulseArticles,
  activeArticle,
  handleNavigateLeftCard,
  handleNavigateRightCard,
  backToBt = "/",
}: PulseDesktopPageProps): React.ReactElement {
  const { sendCustomEvent } = useSendPulseGaData();

  const totalLength = sortedPulseArticles.length + 1;
  const displayTitle = sortedPulseArticles?.[activeArticle - 1]?.title || "";

  const handleArrowRightClick = () => {
    handleNavigateRightCard();

    sendCustomEvent(`desktop::click >::${activeArticle}`, displayTitle);
  };

  const handleArrowLeftClick = () => {
    handleNavigateLeftCard();

    sendCustomEvent(`desktop::click <::${activeArticle}`, displayTitle);
  };

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const { code } = e;

      if (code === "ArrowRight") {
        if (activeArticle >= totalLength) return;

        handleNavigateRightCard();
        sendCustomEvent(
          `desktop::keyboard press right::${activeArticle}`,
          displayTitle
        );
      }

      if (code === "ArrowLeft") {
        if (activeArticle === 1) return;

        handleNavigateLeftCard();
        sendCustomEvent(
          `desktop::keyboard press left::${activeArticle}`,
          displayTitle
        );
      }
    },
    [
      handleNavigateRightCard,
      handleNavigateLeftCard,
      activeArticle,
      totalLength,
      displayTitle,
      sendCustomEvent,
    ]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress, true);

    return function () {
      window.removeEventListener("keydown", handleKeyPress, true);
    };
  }, [handleKeyPress, activeArticle, totalLength]);

  if (sortedPulseArticles.length === 0) return <> </>;

  return (
    <div
      data-testid="pulse-desktop-carousel-components"
      className="relative z-10 h-full w-full"
    >
      <div
        className="relative left-1/2 top-1/2 h-[650px] -translate-x-1/2 -translate-y-1/2"
        style={{ width: `${CARD_WIDTH}px` }}
      >
        <div
          className="absolute z-20 h-full min-w-[340px] bg-[#121212]"
          style={{
            left: `calc(-1 * ${300 + ARROW_TOTAL_WIDTH + PADDING * 2}px)`,
          }}
        />

        <button
          className={cn(
            REMOVE_FOCUS_CSS,
            "absolute top-1/2 z-30 -translate-y-1/2 rounded-full bg-[#353535] p-1 transition-all duration-300 hover:bg-[#555555] active:scale-75",
            { "pointer-events-none opacity-0": activeArticle === 1 }
          )}
          style={{ left: `calc(-1 * ${ARROW_SIZE + PADDING}px)` }}
          onClick={handleArrowLeftClick}
        >
          <img
            src={chevronLeft}
            className="h-6 w-6"
            height={24}
            width={24}
            alt="left arrow"
          />
        </button>

        <button
          className={cn(
            REMOVE_FOCUS_CSS,
            "absolute top-1/2 z-30 -translate-y-1/2 rounded-full bg-[#353535] p-1 transition-all duration-300 hover:bg-[#555555] active:scale-75",
            {
              "pointer-events-none opacity-0": activeArticle === totalLength,
            }
          )}
          style={{ right: `calc(-1 * ${ARROW_SIZE + PADDING}px)` }}
          onClick={handleArrowRightClick}
        >
          <img
            src={chevronRight}
            className="h-6 w-6"
            height={24}
            width={24}
            alt="right arrow"
          />
        </button>

        <div className="absolute -top-4 left-1/2 z-10 flex -translate-x-1/2 justify-center pb-4">
          {Array.from({ length: totalLength }).map((_, index) => {
            return (
              <div
                key={`pulse-desktop-carousel-pagination-${index}`}
                className={cn(
                  "mx-1 h-1 w-1 rounded-full bg-gray-650 transition-all duration-700",
                  { "bg-white": index + 1 <= activeArticle },
                  {
                    "w-4 bg-white": index + 1 === activeArticle,
                  }
                )}
              />
            );
          })}
        </div>

        {Array.from({ length: totalLength }).map((_, index) => {
          const article = sortedPulseArticles[index];
          const id = article?.id || "finished";

          return (
            <div
              key={id}
              id={id}
              className="group absolute flex h-full items-center justify-center text-white transition-[left] duration-300"
              style={{ left: getTheCardLeftCss(activeArticle, index) }}
              data-is-active={activeArticle - 1 === index}
              data-is-prev={activeArticle - 1 > index}
              data-is-next={activeArticle - 1 < index}
            >
              {index + 1 === totalLength ? (
                <PulseEndingScreen
                  key="ending-screen"
                  cardWidth={CARD_WIDTH}
                  smallCardWidth={SMALL_CARD_WIDTH}
                  activeArticle={activeArticle}
                  index={index}
                  backToBt={backToBt}
                />
              ) : (
                <PulseIndividualArticle
                  article={article}
                  cardWidth={CARD_WIDTH}
                  smallCardWidth={SMALL_CARD_WIDTH}
                  activeArticle={activeArticle}
                  index={index}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
